'use client';

import { useModals } from '@/contexts/ModalContext';
import { TelegramProductExtendedDto, useTelegramProductsUserGetContacts } from '@/generated';
import { useTranslations } from 'next-intl';
import { useMediaQuery } from 'usehooks-ts';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getProductContactsDialPhoneNumber, getProductContactsDisplayPhoneNumber } from '@/utils/renderPhoneNumber';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import Image from 'next/image';

type Props = {
  onOk: () => void;
  product: TelegramProductExtendedDto;
};

export const CallToUserModal: React.FC<Props> = ({ onOk, product }) => {
  const modals = useModals();
  const t = useTranslations('modals');

  const { data: contacts, isLoading } = useTelegramProductsUserGetContacts(
    product.link,
    { contactAction: 'phone' },
    { query: { staleTime: Infinity }, client: { handle401: false } },
  );

  const displayPhoneNumber = getProductContactsDisplayPhoneNumber(contacts);
  const dialPhoneNumber = getProductContactsDialPhoneNumber(contacts);

  const onClose = () => {
    onOk();
    modals.close(`call-user-modal`);
  };

  const isMobile = useMediaQuery('(max-width: 767px)');

  const content = (
    <>
      <div className='flex flex-col gap-[16px] items-center'>
        <Avatar className='size-[44px] self-center'>
          <AvatarImage src={product.userPhoto ?? undefined} alt='' />
          <AvatarFallback className='bg-primary-green-day text-white text-[20px]'>
            {product.userName?.charAt(0)}
          </AvatarFallback>
        </Avatar>

        <div className='text-center text-m font-semibold!'>{product.userName || ''}</div>
      </div>
      <div className='text-s text-text-disabled-day text-center '>
        {t('callModal.datestart', { date: contacts?.createdAt ? formatDate(contacts.createdAt) : '' })}
      </div>

      {isLoading ? (
        <Skeleton className='w-[100px] h-[20px] self-center' />
      ) : (
        <a
          href={dialPhoneNumber ? `tel:${dialPhoneNumber}` : undefined}
          className='self-center text-[16px] font-semibold text-primary text-center underline-offset-4 hover:text-main-green hover:underline'
        >
          {displayPhoneNumber}
        </a>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer open onOpenChange={(open) => !open && onClose()}>
        <DrawerContent className='rounded-t-[24px] bg-bg-day'>
          <DrawerHeader className='items-center px-[16px] pt-[16px] pb-[8px] text-center md:text-center'>
            <DrawerTitle className='text-xl-heading text-text-primary-day'>{t('callModal.header')}</DrawerTitle>
            <DrawerDescription className='text-s'>{t('callModal.sub_header')}</DrawerDescription>
          </DrawerHeader>
          <div className='flex flex-col gap-[16px] px-[16px] pt-[8px] pb-[calc(24px+env(safe-area-inset-bottom))]'>
            {content}
            {isLoading ? (
              <Skeleton className='h-[56px] w-full rounded-[12px]' />
            ) : (
              dialPhoneNumber && (
                <Button asChild className='h-[56px] w-full gap-[8px] rounded-[12px] text-[16px] font-medium text-white'>
                  <a href={`tel:${dialPhoneNumber}`}>
                    <Image src='/assets/icons/icon-call-to.svg' width={16} height={16} alt='' />
                    {t('callModal.callBtn')}
                  </a>
                </Button>
              )
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-[400px] rounded-[24px]'>
        <DialogHeader className='items-center text-center sm:text-center'>
          <DialogTitle>{t('callModal.header')}</DialogTitle>
          <DialogDescription>{t('callModal.sub_header')}</DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
};

const formatDate = (iso: string) => {
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};
